/* eslint-disable object-curly-spacing */ 
/* eslint-disable no-undef */ 
/* eslint-disable own-handles */ 
/* eslint-disable no-innerhtml-assign */ 
/* eslint-disable consistent-method-names */
/* eslint-disable no-jsdoc */
/**
 * This class makes it easy to generate URL string to request a static map image using the TomTom's
 * [Static Image API](https://developer.tomtom.com/online-maps/online-maps-documentation-raster/static-image).
 *
 * Parameters can be passed to the constructor or provided via convenient methods that can be chained until
 * the method {{#crossLink "StaticMapImage/go:method"}}{{/crossLink}} generates the URL string.
 *
 * @example
 * ```js
 * // Using chainable methods
 * function callbackFn(urlString) {
 *   console.log(urlString);
 * }
 * var sMap = tomtom.staticMapImage();
 * sMap.key('abdcef123456')
 *   .zoom(15)
 *   .center(L.latLng(50.5, 30.5))
 *   .width(600)
 *   .height(400)
 *   .format('jpg')
 *   .go()
 *   .then(callbackFn);
 * ```
 *
 * @example
 * ```js
 * // Passing a object with all the configuration
 * function callbackFn(urlString) {
 *   console.log(urlString);
 * }
 * tomtom.staticMapImage({
 *   key: 'abdcef123456',
 *   zoom: 15,
 *   bbox: L.latLngBounds([
 *     [40.712, -74.227],
 *     [40.774, -74.125]
 *   ]),
 * }).go(callbackFn);
 * ```
 *
 * @class StaticMapImage
 * @uses Chain
 * @module Services
 * @constructor
 * @param {Object} [options]
 * @param {"basic"|"hybrid"|"labels"} [options.layer="basic"] The base map's layer to be used
 * @param {"main"|"night"} [options.style="main"] The base map style to be used.
 * @param {"png"|"jpg"|"jpeg"} [options.format="png"] The format of the image to be requested
 * @param {String} [options.key] API key that can be obtained from
 *     <a href="http://developer.tomtom.com">TomTom's developer portal</a>.
 * @param {Number} [options.zoom=12] Positive integer between 0 and 20
 * @param {L.LatLng} [options.center] Coordinates to the center of the view
 * @param {Number} [options.width] Width of the image in pixels
 * @param {Number} [options.height] Height of the image in pixels
 * @param {L.LatLngBounds} [options.bbox] Bounding box
 * @param {"Unified"|"IN"} [options.view="Unified"] Geopolitical view
 * @param {String} [options.language="NGT"] Language to use for the labels.
 *  You can found the list of supported languages
 *  <a href="https://developer.tomtom.com/online-maps/online-maps-documentation-vector/tile#listOfSupportedLanguages">
 *  here</a>.
 * @param {Function} [options.callback] Callback function to use in case of success
 * @param {Function} [options.fail=default] Callback function to use in case of failure
 */
define(['display/map',
    'services/utils',
    'services/chain',
    'services/validators',
    'services/converters',
    'mainConfig'],
function(L,
    Utils,
    chain,
    validators,
    converters,
    mainConfig) {
    'use strict';

    /**
     * @ignore
     * Static Map API endpoint.
     */
    var baseUrl = mainConfig['endpoints.staticMapImage'];
    var MAX_WIDTH = 8192;
    var MAX_HEIGHT = 8192;
    var SUPPORTED_IMAGE_FORMATS_PER_LAYER = {
        'basic': ['jpg', 'jpeg', 'png'],
        'hybrid': ['png'],
        'labels': ['png']
    };

    /**
     * Getter and setter for the option `layer`.
     * @method layer
     * @chainable
     * @param {"basic"|"hybrid"|"labels"} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `style`.
     * @method style
     * @chainable
     * @param {"main"|"night"} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `format`.
     * @method format
     * @chainable
     * @param {"png"|"jpg"|"jpeg"} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `key`.
     * @method key
     * @chainable
     * @param {String} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `zoom`.
     * @method zoom
     * @chainable
     * @param {Number} [newValue] New integer value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `center`.
     * @method center
     * @chainable
     * @param {L.LatLng} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `width`.
     * @method width
     * @chainable
     * @param {Number} [newValue] New integer value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `height`.
     * @method height
     * @chainable
     * @param {Number} [newValue] New integer value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `bbox`.
     * @method bbox
     * @chainable
     * @param {L.LatLngBounds} [newValue] New value to be set
     * @return {Object} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `view`.
     * @method view
     * @chainable
     * @param {"Unified"|"IN"} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    /**
     * Getter and setter for the option `language`.
     * @method language
     * @chainable
     * @param {String} [newValue] New value to be set
     * @return {String} When is used as setter this method is chainable
     */

    var fields = {
        layer: {validator: validators.oneOfValue(['basic', 'hybrid', 'labels'], 'layers'), defaultValue: 'basic'},
        style: {validator: validators.oneOfValue(['main', 'night'], 'styles'), defaultValue: 'main'},
        format: {validator: validators.oneOfValue(['png', 'jpg', 'jpeg'], 'formats'), defaultValue: 'png'},
        key: {validator: validators.string, required: true},
        zoom: {validator: validators.integerInInterval(-1, 20), converter: converters.integer},
        center: {converter: converters.point, validator: validators.point},
        width: {validator: validators.integerInInterval(0, MAX_WIDTH)},
        height: {validator: validators.integerInInterval(0, MAX_HEIGHT)},
        bbox: {validator: validators.boundingBox, converter: converters.boundingBox},
        view: {validator: validators.geopolView},
        language: {validator: validators.locale('maps')}
    };

    function checkIfLayerSupported(data) {
        if (!SUPPORTED_IMAGE_FORMATS_PER_LAYER[data.layer] ||
            SUPPORTED_IMAGE_FORMATS_PER_LAYER[data.layer].indexOf(data.format) === -1) {
            throw new Error('Unsupported layer. Please change to PNG or use basic layer.');
        }
    }

    function checkIfBBoxAndCenterSet(data) {
        if (data.bbox && data.center) {
            // If both are used - service will return a HTTP 400 response.
            throw new Error('The bbox and center properties cannot be used together');
        }
    }

    function checkIfBBoxAndCenterNotSet(data) {
        if (!data.bbox && !data.center) {
            throw new Error('Either bbox or center property must be provided');
        }
    }

    function checkIfBBoxUsedWithHeightOrWidth(data) {
        if (data.bbox && (Utils.isValidNumber(data.width) || Utils.isValidNumber(data.height))) {
            // If both are used - service will return a HTTP 400 response.
            throw new Error('The bbox, width and height properties cannot be used together');
        }
    }

    /**
     * @ignore
     * Validate combination of properties. Done here because chain.js does not support such behaviour
     * @throws Error
     * @param {Object} data
     */
    function validateCombineProperties(data) {

        // Executing additional more complex validations
        checkIfLayerSupported(data);
        checkIfBBoxAndCenterSet(data);
        checkIfBBoxAndCenterNotSet(data);
        checkIfBBoxUsedWithHeightOrWidth(data);
    }

    function convertBBoxToString(bbox) {
        return bbox.minLon + ',' + bbox.minLat + ',' + bbox.maxLon + ',' + bbox.maxLat;
    }

    function serializeToStringIfNecessary(propertyName, propertyValue) {
        var splitCenter, latLngCenter;
        if (propertyName === 'bbox') {
            propertyValue = convertBBoxToString(propertyValue);
        } else if (propertyName === 'zoom') {
            return String(parseInt(propertyValue, 10) - 1);
        } else if (propertyName === 'center') {
            splitCenter = propertyValue.split(',');
            latLngCenter = L.latLng(parseFloat(splitCenter[0]), splitCenter[1]);
            return latLngCenter.lng + ',' + latLngCenter.lat;
        }
        return propertyValue;
    }

    /**
     * @ignore
     * Build the Url string using the configuration available.
     * @param {Object} data
     * @return {String}
     */
    function toUrl(data) {
        var querySegments = Object.keys(fields).reduce(function(segments, propertyName) {
            var propertyValue = data[propertyName];
            if (propertyValue !== undefined) {
                propertyValue = serializeToStringIfNecessary(propertyName, propertyValue);
                segments.push(propertyName + '=' + encodeURI(propertyValue));
            }
            return segments;
        }, []);
        return baseUrl + '?' + querySegments.join('&');
    }

    function handleChain(data, callback) {
        validateCombineProperties(data);
        if (callback) {
            callback(toUrl(data));
        }
    }

    function StaticMapImage(initConfig) {
        return chain.call(this, Utils.clone(fields), handleChain, initConfig);
    }

    return StaticMapImage;
});



// WEBPACK FOOTER //
// ./services/staticMapImage/staticMapImage.js